import React, { useState } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

/**
 * Two steps: parse the file into a server-side preview, then confirm.
 * Nothing is visible to anyone until "Save" — a wrong branch or a truncated
 * export is caught here, not after people have looked at it.
 *
 * Supplier coverage is shown as information only. Unmapped items are expected
 * and appear under "Unmapped supplier"; they never block a save.
 */
const money = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StockUploadModal = ({ businessUnits, defaultBranchId, onClose, onSaved }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [branchId, setBranchId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const check = async () => {
        if (!file) return;
        setBusy(true);
        setError('');
        try {
            const res = await stockReorderApi.uploadStockReport(file);
            setPreview(res);
            const suggested = res.suggested_business_unit_id || defaultBranchId || '';
            setBranchId(suggested);
            if (!res.suggested_business_unit_id && suggested) {
                setPreview({ ...res, ...(await stockReorderApi.stockPreviewForBranch(res.id, suggested)) });
            }
        } catch (err) {
            setError(errorMessage(err, 'Could not read that file'));
        } finally {
            setBusy(false);
        }
    };

    const changeBranch = async (id) => {
        setBranchId(id);
        if (!id || !preview) return;
        setBusy(true);
        setError('');
        try {
            const ctx = await stockReorderApi.stockPreviewForBranch(preview.id, id);
            setPreview((p) => ({ ...p, ...ctx }));
        } catch (err) {
            setError(errorMessage(err, 'Could not check that branch'));
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        setBusy(true);
        setError('');
        try {
            onSaved(await stockReorderApi.confirmStockUpload(preview.id, branchId));
        } catch (err) {
            setError(errorMessage(err, 'Could not save the stock report'));
            setBusy(false);
        }
    };

    const matched = preview && preview.suggested_business_unit_id === branchId && branchId;

    return (
        <div className="sr-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
            <div className="sr-modal" role="dialog" aria-modal="true" aria-labelledby="sr-upload-title">
                <div className="sr-modal-head">
                    <h2 id="sr-upload-title" className="sr-card-title">
                        {preview ? 'Check before saving' : 'Upload stock report'}
                    </h2>
                    <button className="sr-link" onClick={onClose} disabled={busy} aria-label="Close">
                        ✕
                    </button>
                </div>

                {!preview && (
                    <>
                        <div className="sr-field">
                            <label htmlFor="sr-stock-file">ERP "Stock Report" file</label>
                            <input
                                id="sr-stock-file"
                                type="file"
                                accept=".xls,.xlsx,.xlsm,.htm,.html,.csv"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <span className="sr-hint">.xls or .xlsx exported from the ERP · max 50 MB</span>
                        </div>
                        <div className="sr-actions">
                            <button className="sr-btn" onClick={onClose} disabled={busy}>
                                Cancel
                            </button>
                            <button className="sr-btn sr-btn-primary" onClick={check} disabled={!file || busy}>
                                {busy ? 'Reading file…' : 'Check file'}
                            </button>
                        </div>
                    </>
                )}

                {preview && (
                    <>
                        <dl className="sr-preview-facts">
                            <dt>File</dt>
                            <dd>{preview.filename}</dd>
                            <dt>Branch</dt>
                            <dd>
                                {preview.location_label && (
                                    <span className="sr-muted">{preview.location_label} → </span>
                                )}
                                <select value={branchId} onChange={(e) => changeBranch(e.target.value)} disabled={busy}>
                                    <option value="">Select branch…</option>
                                    {businessUnits.map((bu) => (
                                        <option key={bu.id} value={bu.id}>
                                            {bu.name}
                                        </option>
                                    ))}
                                </select>
                                {matched && <span className="sr-tag sr-tag-ok">matched</span>}
                            </dd>
                            <dt>As on</dt>
                            <dd>{preview.as_on_date}</dd>
                            <dt>Contents</dt>
                            <dd>
                                {preview.item_count.toLocaleString()} items ·{' '}
                                {preview.manufacturer_count.toLocaleString()} manufacturers · qty{' '}
                                {Number(preview.total_qty).toLocaleString(undefined, { maximumFractionDigits: 3 })} ·
                                value {money(preview.total_value)}
                            </dd>
                        </dl>

                        {preview.totals_checked && (
                            <div className="sr-alert sr-alert-ok">✓ Item rows match the report's Grand Total</div>
                        )}
                        {preview.warnings.map((w) => (
                            <div key={w} className="sr-alert sr-alert-warn">
                                {w}
                            </div>
                        ))}
                        {branchId && preview.mapped_count != null && (
                            <div className="sr-alert sr-alert-info">
                                ⓘ {preview.mapped_count.toLocaleString()} items have a supplier ·{' '}
                                {(preview.item_count - preview.mapped_count).toLocaleString()} will show under
                                "Unmapped supplier" until Supplier Mapping covers them.
                            </div>
                        )}
                        {branchId && preview.duplicate_file && (
                            <div className="sr-alert sr-alert-warn">This exact file was already uploaded for this branch.</div>
                        )}
                        {branchId && preview.same_date_exists && (
                            <div className="sr-alert sr-alert-warn">
                                A report as on {preview.as_on_date} already exists for this branch — saving replaces it.
                            </div>
                        )}
                        {branchId && preview.older_than_current && (
                            <div className="sr-alert sr-alert-warn">
                                This report is older than the branch's current stock, so it is saved to history only.
                            </div>
                        )}

                        <details className="sr-details">
                            <summary>First {preview.preview_rows.length} rows</summary>
                            <div className="sr-table-wrap">
                                <table className="sr-table sr-preview-table">
                                    <thead>
                                        <tr>
                                            <th>Manufacturer</th>
                                            <th>Part No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th className="sr-right">Qty</th>
                                            <th className="sr-right">Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.preview_rows.map((r) => (
                                            <tr key={r.part_key}>
                                                <td>{r.manufacturer}</td>
                                                <td>{r.part_number}</td>
                                                <td className="sr-wrap">{r.description}</td>
                                                <td>{r.unit}</td>
                                                <td className="sr-right">{r.qty}</td>
                                                <td className="sr-right">{money(r.value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>

                        <div className="sr-actions sr-modal-actions">
                            <button
                                className="sr-btn"
                                onClick={() => {
                                    setPreview(null);
                                    setError('');
                                }}
                                disabled={busy}
                            >
                                Back
                            </button>
                            <button className="sr-btn sr-btn-primary" onClick={save} disabled={!branchId || busy}>
                                {busy ? 'Saving…' : preview.older_than_current ? 'Save to history' : 'Save as current stock'}
                            </button>
                        </div>
                    </>
                )}

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
            </div>
        </div>
    );
};

export default StockUploadModal;
